import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import DialogNames from '~/constants/DialogNames'
import { getLabOrderStatuses } from '~/store/reducers/constants'
import useDialog from '~/utils/useDialog'

import { AgentChatLinkButton, getChildrenText } from './AgentChatLinkButton'
import {
  getOrderResultsDialogParams,
  parseLabTestDashboardUrl,
} from './labTestLinkUtils'

export interface LabTestLinkButtonProps {
  children?: React.ReactNode
  className?: string
  href: string
  onLinkClick?: () => void
}

/**
 * Lab test link button: opens OrderResultsDialog when URL has all required params,
 * otherwise opens lab tests dashboard in a new tab.
 */
export const LabTestLinkButton: React.FC<LabTestLinkButtonProps> = ({
  href,
  children,
  className,
  onLinkClick,
}) => {
  const { t } = useTranslation('Common')
  const labOrderStatuses = useSelector(getLabOrderStatuses)
  const [openOrderResultsDialog] = useDialog(DialogNames.ORDER_RESULTS)

  const parsed = parseLabTestDashboardUrl(href)
  const dialogParams =
    parsed &&
    getOrderResultsDialogParams(
      parsed.labTestIdentifier,
      parsed.queryParams,
      labOrderStatuses,
    )

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    onLinkClick?.()
    if (dialogParams) {
      openOrderResultsDialog({
        canComplete: dialogParams.canComplete,
        hasResults: dialogParams.hasResults,
        orderId: dialogParams.orderId,
        pdfUrl: dialogParams.pdfUrl,
        vendorId: dialogParams.vendorId,
      })
    } else {
      window.open(href, '_blank')
    }
  }

  const tooltipContent = t('Common:VIEW_LAB_TEST_RESULTS')
  const ariaLabel = getChildrenText(children) || tooltipContent

  return (
    <AgentChatLinkButton
      ariaLabel={ariaLabel}
      className={className}
      tooltipContent={tooltipContent}
      onClick={handleClick}
    />
  )
}
